<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteClient"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <delete-modal
        v-if="addressIdToDelete"
        :loading="loadingDelete"
        @close="addressIdToDelete = null"
        @confirmDelete="deleteAddress"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <wallet-adjust-modal
        v-if="showWalletAdjustModal"
        :balance="wallet ? wallet.balance : 0"
        :loading="loadingWalletAdjust"
        @close="showWalletAdjustModal = false"
        @confirm="adjustWallet"
      ></wallet-adjust-modal>
    </transition>

    <div v-if="!loading">
      <div v-if="client" class="flex flex-col">
        <div class="card">
          <card-header :title="'Cliente #' + client.id">
            <template #badge>
              <status-badge
                v-if="client.user.verified"
                status="active"
                label="Verificado"
              />
              <status-badge
                v-else
                status="canceled"
                label="No Verificado"
              />
            </template>
            <template #actions>
              <button
                @click="impersonateClient"
                class="primary-button mx-0 text-sm"
                :disabled="loadingImpersonate"
              >
                {{ loadingImpersonate ? 'Cargando...' : 'Entrar como Cliente' }}
              </button>

              <router-link
                v-if="hasPermission('edit_roles')"
                :to="{
                  name: 'clientsEditForm',
                  params: { id: client.id },
                }"
              >
                <edit-button />
              </router-link>

              <delete-button
                v-if="hasPermission('delete_roles')"
                class="delete-icon-button"
                @click.prevent="openDeleteModal()"
              />
            </template>
          </card-header>

          <div v-if="client.profile_image" class="w-20 h-20 relative flex">
            <img
              ref="imagePreview"
              class="rounded-full object-cover object-center w-full h-full border border-primary-500"
              :src="client.profile_image.url"
            />
          </div>

          <section-header title="Datos de Contacto" />

          <div class="detail-grid">
            <detail-field label="Nombre" :value="client.name + ' ' + client.last_name" />

            <detail-field label="Correo">
              <a
                :href="`mailto:${client.user.email}`"
                class="system-button underline"
                >{{ client.user.email }}</a
              >
            </detail-field>

            <detail-field label="Teléfono">
              <a
                :href="`https://api.whatsapp.com/send?phone=${client.phone}`"
                target="_blank"
                class="system-button underline"
              >
                {{ client.phone }}
              </a>
            </detail-field>

            <detail-field
              label="Autenticación"
              :value="client.user.external_auth
                ? capitalizeFirstLetter(client.user.external_auth)
                : 'Correo'"
            />
          </div>

          <section-header title="Informacion Personal" />

          <div class="detail-grid">
            <detail-field label="Género" :value="client.gender" />
            <detail-field label="Fecha de nacimiento" :value="formatDate(client.birthdate)" />
            <detail-field v-if="client.branch" label="Sucursal" :value="capitalizeFirstLetter(client.branch.name)" />
            <detail-field label="Fecha de creación" :value="formatDatetime(client.created_at)" />
            <detail-field label="Fecha de última actualización" :value="formatDatetime(client.updated_at)" />
          </div>

          <section-header title="Configuración de Pago" />

          <div class="flex items-center gap-3 text-sm">
            <custom-toggle
              :modelValue="client.allows_cash_payment"
              :disabled="!hasPermission('edit_roles') || togglingCash"
              @change="toggleCashEligibility"
            />
            <div>
              <p class="detail-label">Pago en efectivo</p>
              <p class="detail-value">
                {{ client.allows_cash_payment
                  ? 'El cliente puede pagar planes en efectivo (bloques prepagados).'
                  : 'El cliente paga con tarjeta vía Stripe.' }}
              </p>
            </div>
          </div>

          <section-header title="Mascotas" />

          <div class="flex flex-wrap">
            <div v-for="pet in client.pets" :key="pet.id" class="mr-4">
              <router-link
                :to="{
                  name: 'petsDetail',
                  params: { id: pet.id },
                }"
                class="underline system-button"
                >{{ pet.name }}</router-link
              >
            </div>
          </div>

          <section-header title="Planes" />

          <div class="flex flex-wrap">
            <div v-for="quote in client.quotes" :key="quote.id" class="mr-4">
              <router-link
                :to="{
                  name: 'quotesDetail',
                  params: { id: quote.id },
                }"
                class="underline system-button"
                >{{ getPlanName(quote) }}</router-link
              >
            </div>
          </div>

          <template v-if="hasPermission('view_wallets')">
            <section-header title="Monedero" />

            <div v-if="wallet">
              <div class="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p class="detail-label">Saldo disponible</p>
                  <p class="detail-value font-semibold">
                    {{ integerToCurrency(wallet.balance) }}
                  </p>
                </div>

                <button
                  v-if="hasPermission('edit_wallets')"
                  class="primary-button mx-0 text-sm"
                  @click="showWalletAdjustModal = true"
                >
                  Registrar ajuste
                </button>
              </div>

              <p v-if="!wallet.entries.length" class="text-sm text-content-400 mt-2">
                Sin movimientos en el monedero.
              </p>

              <div v-else class="overflow-x-auto">
                <table class="table-auto text-sm mt-2 w-full">
                  <thead class="text-left">
                    <tr class="bg-primary-100">
                      <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Fecha</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Tipo</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Monto</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Descripción</th>
                      <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Vence</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in wallet.entries" :key="entry.id" class="table-row">
                      <td class="table-cell whitespace-nowrap">{{ formatDatetime(entry.created_at) }}</td>
                      <td class="table-cell">{{ walletTypeLabel(entry.type) }}</td>
                      <td
                        class="table-cell whitespace-nowrap"
                        :class="walletAmountClass(entry)"
                      >
                        {{ walletSignedAmount(entry) }}
                      </td>
                      <td class="table-cell">{{ entry.description }}</td>
                      <td class="table-cell whitespace-nowrap">
                        {{ entry.expires_at ? formatDate(entry.expires_at) : '-' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p v-else-if="walletError" class="text-sm text-content-400 mt-2">
              {{ walletError }}
            </p>

            <section-header title="Referidos" />

            <div v-if="wallet">
              <div v-if="wallet.referred_by" class="text-sm mb-2">
                <p class="detail-label">Referido por</p>
                <p class="detail-value">
                  <router-link
                    :to="{ name: 'clientsDetail', params: { id: wallet.referred_by.referrer_client_id } }"
                    class="underline system-button"
                  >{{ wallet.referred_by.referrer_name }}</router-link>
                </p>
              </div>

              <p v-if="!wallet.referrals.length" class="text-sm text-content-400 mt-2">
                Este cliente no ha referido a nadie.
              </p>

              <div v-else class="overflow-x-auto">
                <table class="table-auto text-sm mt-2 w-full">
                  <thead class="text-left">
                    <tr class="bg-primary-100">
                      <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Referido</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Recompensa</th>
                      <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Registrado</th>
                      <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Convertido</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="referral in wallet.referrals" :key="referral.id" class="table-row">
                      <td class="table-cell">{{ referral.referred_name }}</td>
                      <td class="table-cell">
                        <status-badge
                          :status="referralBadgeStatus(referral.status)"
                          :label="referralStatusLabel(referral.status)"
                        />
                      </td>
                      <td class="table-cell whitespace-nowrap">
                        {{ referral.reward_amount !== null ? integerToCurrency(referral.reward_amount) : '-' }}
                      </td>
                      <td class="table-cell whitespace-nowrap">{{ formatDate(referral.created_at) }}</td>
                      <td class="table-cell whitespace-nowrap">
                        {{ referral.converted_at ? formatDate(referral.converted_at) : '-' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>

          <section-header title="Cuestionario" />

          <div class="text-sm mt-2">
            <p class="detail-label">Beneficios esperados</p>
            <p v-for="benefit in getExpectations('benefit')" :key="benefit.id" class="detail-value">
              - {{ benefit.value }}
            </p>
          </div>

          <div v-if="getExpectations('barf_opinion')[0]" class="mt-2">
            <detail-field label="Opinión sobre dieta BARF" :value="getExpectations('barf_opinion')[0].value" />
          </div>

          <div v-if="getExpectations('bond')[0]" class="mt-2">
            <detail-field label="Vínculo con mascota" :value="getExpectations('bond')[0].value" />
          </div>
        </div>

        <h2 class="mt-6">Direcciones</h2>

        <router-link
          v-if="hasPermission('edit_addresses')"
          :to="{ name: 'addressCreateForm', params: { clientId: client.id } }"
          class="primary-button mx-0 mt-2"
          >Agregar Dirección</router-link
        >

        <div class="flex flex-wrap">
          <div
            v-for="address in client.addresses"
            :key="address.id"
            class="card w-full sm:w-72 sm:mr-4 mt-6"
          >
            <div class="flex justify-between items-center">
              <p class="font-medium">ID: #{{ address.id }}</p>

              <div class="flex space-x-2 items-center">
                <router-link
                  v-if="hasPermission('edit_addresses')"
                  :to="{
                    name: 'addressEditForm',
                    params: { id: address.id },
                  }"
                >
                  <edit-button />
                </router-link>

                <delete-button
                  v-if="hasPermission('delete_addresses')"
                  @click.prevent="addressIdToDelete = address.id"
                />
              </div>
            </div>

            <div class="div-line"></div>

            <address-component
              :address="address"
              :showShippingPrice="true"
              :branchId="client.branch_id"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import AddressComponent from "@/components/ui/Address";
import { clientUrl as runtimeClientUrl } from "@/runtimeConfig";
import AddressModal from "@/components/modals/AddressModal";
import CustomToggle from "@/components/ui/CustomToggle";
import WalletAdjustModal from "@/components/modals/WalletAdjustModal";
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import { formatDate, formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";

const WALLET_TYPE_LABELS = {
  credit: "Abono",
  debit: "Cargo",
  expiry: "Saldo expirado",
  adjustment: "Ajuste manual",
  reservation: "Reserva (tienda)",
  release: "Reserva liberada",
};

const REFERRAL_STATUS = {
  pending: { badge: "pending", label: "Invitado" },
  signed_up: { badge: "scheduled", label: "Registrado" },
  converted: { badge: "active", label: "Convertido" },
};

export default {
  name: "Client Detail",

  components: {
    AddressComponent,
    AddressModal,
    CustomToggle,
    WalletAdjustModal,
  },

  data() {
    return {
      client: null,
      loading: false,
      loadingDelete: false,
      loadingImpersonate: false,
      addressIdToDelete: null,
      togglingCash: false,
      wallet: null,
      walletError: null,
      showWalletAdjustModal: false,
      loadingWalletAdjust: false,
    };
  },

  mixins: [deleteMixin],

  created() {
    this.$store.commit("setSelectedTab", "clients");

    this.loadClient();
    this.loadWallet();
  },

  watch: {
    // Los links "Referido por" navegan clientsDetail → clientsDetail; el
    // componente se reutiliza y created() no vuelve a correr.
    "$route.params.id"() {
      if (this.$route.name === "clientsDetail") {
        this.loadClient();
        this.loadWallet();
      }
    },
  },

  methods: {
    formatDate,

    formatDatetime,

    integerToCurrency,

    async loadWallet() {
      if (!this.hasPermission("view_wallets")) return;

      this.wallet = null;
      this.walletError = null;

      try {
        this.wallet = await api.clients.getWallet(this.$route.params.id);
      } catch (error) {
        this.walletError =
          error?.response?.data?.message || "No se pudo cargar el monedero.";
      }
    },

    walletTypeLabel(type) {
      return WALLET_TYPE_LABELS[type] || type;
    },

    // Abonos y liberaciones suman; ajustes llevan su propio signo; cargos,
    // expiraciones y reservas restan del saldo disponible.
    walletEntryIsNegative(entry) {
      if (entry.type === "credit" || entry.type === "release") {
        return false;
      }
      if (entry.type === "adjustment") {
        return entry.amount < 0;
      }
      return true;
    },

    walletSignedAmount(entry) {
      const value = integerToCurrency(Math.abs(entry.amount));
      return this.walletEntryIsNegative(entry) ? `- ${value}` : `+ ${value}`;
    },

    walletAmountClass(entry) {
      return this.walletEntryIsNegative(entry)
        ? "text-red-600"
        : "text-green-600";
    },

    referralBadgeStatus(status) {
      return (REFERRAL_STATUS[status] || { badge: status }).badge;
    },

    referralStatusLabel(status) {
      return (REFERRAL_STATUS[status] || { label: status }).label;
    },

    async adjustWallet(form) {
      this.loadingWalletAdjust = true;

      try {
        const response = await api.clients.adjustWallet(
          this.client.id,
          form
        );
        this.$store.dispatch("successSnack", response.message);
        this.showWalletAdjustModal = false;
        await this.loadWallet();
      } catch (error) {
        this.$store.dispatch(
          "errorSnack",
          error?.response?.data?.message || "No se pudo registrar el ajuste. Intenta de nuevo."
        );
      }

      this.loadingWalletAdjust = false;
    },

    async loadClient() {
      this.loading = true;

      try {
        this.client = await api.clients.getById(this.$route.params.id);
        this.$store.commit('setBreadcrumbLabel', {
          routeName: this.$route.name,
          label: this.client.name + ' ' + this.client.last_name,
        });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: 'clientsIndex' });
      }

      this.loading = false;
    },

    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    getExpectations(key) {
      return this.client.expectations.filter((ex) => ex.type === key);
    },

    getPlanName(quote) {
      const petNames = quote.diets
        .filter((diet) => diet.status !== "excluded")
        .map((diet) => diet.pet?.name)
        .filter(Boolean)
        .join(" - ");

      return `#${quote.id} / ${petNames}`;
    },

    async deleteClient() {
      this.loadingDelete = true;

      try {
        const response = await api.clients.delete(this.client.id);
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
        this.$router.push({ name: 'clientsIndex' });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingDelete = false;
    },

    async deleteAddress() {
      this.loadingDelete = true;

      try {
        const response = await api.clients.deleteAddress(
          this.addressIdToDelete
        );
        this.$store.dispatch("successSnack", response.message);
        this.loadClient();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.addressIdToDelete = null;
      this.loadingDelete = false;
    },

    async toggleCashEligibility() {
      if (this.togglingCash) return;

      const newValue = !this.client.allows_cash_payment;
      this.togglingCash = true;

      try {
        const response = await api.clients.update(this.client.id, {
          name: this.client.name,
          last_name: this.client.last_name,
          email: this.client.user.email,
          phone: this.client.phone,
          gender: this.client.gender,
          birthdate: this.client.birthdate,
          allows_cash_payment: newValue,
        });
        this.client.allows_cash_payment = newValue;
        this.$store.dispatch("successSnack", response.message);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.togglingCash = false;
    },

    async impersonateClient() {
      this.loadingImpersonate = true;

      try {
        const response = await api.clients.impersonate(this.client.id);

        // Open client portal in new tab with impersonation data via URL
        const clientUrl = runtimeClientUrl || 'http://localhost:8081';

        // Encode the data as base64 to pass in URL
        const impersonationData = btoa(JSON.stringify({
          token: response.token,
          user: response.user
        }));

        // Open client portal with impersonation data in URL
        window.open(`${clientUrl}/dashboard/inicio?impersonate=${encodeURIComponent(impersonationData)}`, '_blank');

        this.$store.dispatch("successSnack", "Sesión de cliente iniciada en nueva pestaña");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingImpersonate = false;
    },
  },
};
</script>
